import axios, { AxiosError, AxiosResponse } from 'axios';

// Create axios instance
const API_URL = process.env.REACT_APP_API_URL || 'https://hotels.myabad.com';

// Logout utility that can be called from anywhere
export const performLogout = (reason: 'expired' | 'unauthorized' = 'expired') => {
  console.log('Performing auto-logout due to token expiration');
  // Clear all auth-related data
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  
  // Store logout reason in sessionStorage to show a message on login page
  sessionStorage.setItem('logoutReason', reason === 'expired' ? 'session_expired' : 'unauthorized');
  
  // Redirect to login page
  // Use window.location to ensure a full page reload and clear any React state
  window.location.href = '/login';
};

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if it exists
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    
    // Only set Authorization header if token exists and is not undefined
    if (token && token !== 'undefined') {
      // Ensure the token has the proper Bearer prefix
      config.headers.Authorization = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
      console.log('Setting Authorization header:', config.headers.Authorization);
    } else {
      console.warn('No valid token found in localStorage');
      // Remove Authorization header if it exists to prevent 'Bearer undefined'
      if (config.headers.Authorization) {
        delete config.headers.Authorization;
      }
    }
    
    // Properly format dashboard endpoint URLs
    if (config.url?.startsWith('/dashboard/')) {
      // Make sure we're not duplicating slashes if API_URL ends with a slash
      config.url = `${config.url}`;
    }
    
    console.log('Request URL:', `${API_URL}${config.url}`);
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle response errors
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config;
    
    console.error('API Error:', error.message);
    if (error.response) {
      console.error('Status:', error.response.status);
      console.error('Data:', error.response.data);
    }
    
    // Handle 401 - try to refresh token
    // Skip refresh if this is already a refresh request to prevent infinite loops
    if (error.response?.status === 401 && originalRequest && !originalRequest.url?.includes('/auth/token/refresh/')) {
      const refreshToken = localStorage.getItem('refreshToken');
      
      // If we have a refresh token, try to get a new access token
      if (refreshToken) {
        try {
          console.log('Attempting to refresh token...');
          const response = await axios.post(`${API_URL}/auth/token/refresh/`, {
            refresh: refreshToken
          });
          
          const { access } = response.data;
          console.log('Token refreshed successfully');
          localStorage.setItem('token', access);
          
          // Retry original request with new token
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${access}`;
          }
          return api(originalRequest);
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError);
          // If refresh fails, logout user and redirect to login
          performLogout('expired');
          return Promise.reject(refreshError);
        }
      } else {
        // No refresh token available, logout immediately
        console.log('No refresh token available, logging out');
        performLogout('expired');
        return Promise.reject(error);
      }
    }
    
    return Promise.reject(error);
  }
);

// Auth service functions
export const authService = {
  login: async (username: string, password: string) => {
    try {
      console.log('Attempting login for user:', username);
      const response: AxiosResponse = await api.post('/auth/login/', { username, password });
      console.log('Login response:', response.data);
      
      // Validate response structure
      if (!response.data || !response.data.data) {
        console.error('Invalid response structure:', response.data);
        throw new Error('Invalid authentication response structure');
      }
      
      // The tokens and user are nested in the data property
      const { access, refresh, user } = response.data.data;
      
      console.log('Login response details:', { 
        hasAccess: !!access, 
        hasRefresh: !!refresh,
        hasUser: !!user,
        userRole: user?.profile?.role
      });
      
      if (!access || !refresh || !user) {
        console.error('Missing required data:', { access: !!access, refresh: !!refresh, user: !!user });
        throw new Error('Invalid authentication response - missing required data');
      }
      
      // Store tokens
      localStorage.setItem('token', access);
      localStorage.setItem('refreshToken', refresh);
      
      console.log('Login successful, tokens stored');
      console.log('User data:', user);
      
      // Return the full response to be used by Redux
      return response.data;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  },
  
  logout: () => {
    console.log('Logging out user');
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  },
  
  isAuthenticated: () => {
    return !!localStorage.getItem('token');
  }
};

export default api; 